<template>
  <div :class="classes">
    <div v-if="label" class="minus-plus-input__label">
      {{ label }}
    </div>
    <div v-click-outside="blur">
      <div class="minus-plus-input" :class="classesWrap">
        <div v-if="!hideControls && !isConsultant" class="minus-plus-input__btn noselect" @click="dec">
          -
        </div>
        <div :class="{ 'value': true, 'blink': toggled, 'blocked': blocked }">
          <div v-if="!toggled" class="label" @click="focus">
            {{ formatValue(currentValue) + (postfix ? ' ' + postfix : '') }}
          </div>
          <input v-else type="number" v-focus v-model="currentValue">
        </div>
        <div v-if="!hideControls && !isConsultant" class="minus-plus-input__btn noselect" @click="inc">
          +
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const focus = {
  inserted (el) {
    el.focus()
  }
}

export default {
  name: 'UiNumberInput',

  props: {
    max: Number,
    min: Number,
    value: [Number, String],
    postfix: String,
    hideControls: Boolean,
    small: Boolean,
    full: Boolean,
    reactive: Boolean,
    blocked: Boolean,
    label: String,
    medium: Boolean,
    line: Boolean,
    right: Boolean,
    decimalPlaces: {
      type: Number,
      default: 2
    }
  },

  directives: { focus },

  data: () => ({
    toggled: false,
    currentValue: 0
  }),

  mounted () {
    this.currentValue = this.value ? this.value : 0
    window.addEventListener('ui-number-input-focus', this.onOtherFocus)
  },

  beforeDestroy () {
    window.removeEventListener('ui-number-input-focus', this.onOtherFocus)
  },

  computed: {
    classes () {
      return {
        'minus-plus-input--label': this.label,
        'minus-plus-input--medium': this.medium,
        'minus-plus-input--line': this.line,
      }
    },

    classesWrap () {
      return {
        'sm': this.small,
        'full': this.full,
        'minus-plus-input--center': this.isConsultant,
        'minus-plus-input--right': this.right
      }
    }
  },

  methods: {
    formatValue (value) {
      return Number.isInteger(parseFloat(value))
        ? parseInt(value)
        : parseFloat(parseFloat(value).toFixed(this.decimalPlaces))
    },

    restrictDecimals (value) {
      const regex = new RegExp(`^(\\d+)(\\.\\d{0,${this.decimalPlaces}})?`)
      const match = value.toString().match(regex)
      return match ? match[0] : value
    },

    inc () {
      if (!this.max || this.currentValue < this.max) {
        this.currentValue = this.formatValue(+this.currentValue + 1)
        if (!this.toggled) this.toggled = true
      }
    },

    dec () {
      if (!this.min || this.currentValue > this.min) {
        this.currentValue = this.formatValue(+this.currentValue - 1)
        if (!this.toggled) this.toggled = true
      }
    },

    focus (event) {
      event.stopPropagation()
      if (!this.blocked && !this.isConsultant) {
        window.dispatchEvent(new CustomEvent('ui-number-input-focus', { detail: this._uid }))
        this.toggled = true
      }
    },

    blur () {
      if (this.toggled) {
        this.toggled = false
        this.$emit('input', this.formatValue(this.currentValue))
      }
    },

    onOtherFocus (e) {
      if (e.detail !== this._uid && this.toggled) {
        this.toggled = false
        this.$emit('input', this.formatValue(this.currentValue))
      }
    }
  },

  watch: {
    value () {
      this.currentValue = this.formatValue(this.value)
    },

    currentValue (value) {
      const restrictedValue = this.restrictDecimals(value)
      if (restrictedValue !== value) {
        this.currentValue = restrictedValue
      }
      if (this.reactive) {
        this.$emit('input', this.formatValue(restrictedValue))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_mixins.scss';
@import '@/sass/abstracts/_variables.scss';

.minus-plus-input {
  &--label {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__wrapper {
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__label {
    font-size: 1.6rem;
    font-weight: 500;
  }
}

.minus-plus-input {
  min-width: 12rem;
  max-width: 12rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.7rem;
  font-weight: 700;

  &--medium {
    @include mobile {
      min-width: 12rem;
    }
  }

  &--line {
    padding-bottom: .5rem;
    border-bottom: .1rem solid $color-gray-light-2;
  }

  &--center {
    justify-content: center;
  }

  &--right {
    justify-content: flex-end;
  }

  @include mobile {
    min-width: 7.5rem;
  }

  &__btn {
    padding: 0 .5rem;
    cursor: pointer;
  }
}

.minus-plus-input.sm {
  font-size: 1.5rem;
  min-width: 0;
}

.minus-plus-input .blink {
  animation: blink 1s infinite;
}

.minus-plus-input:hover .blink {
  animation: none;
}

.value {
  display: flex;
  justify-content: center;
  padding: 0 .5rem;
  white-space: nowrap;
}

.value .label {
  cursor: pointer;
}

.blocked .label {
  cursor: not-allowed;
  opacity: .4;
}

input {
  text-align: center;
  width: 5.5rem;
  font-size: 1.5rem;

  background: transparent;
}

.minus-plus-input.full {
  width: 100%;
}

.full input {
  width: 100%;
}

@keyframes blink {
  0% {
    opacity: 0
  }

  49% {
    opacity: 0
  }

  50% {
    opacity: 1
  }
}
</style>
